import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState, cloneElement } from 'react';
import { AppIcon, Flex, styled, Tooltip, Theme, AppText } from '@streem/ui-react';
import { recordTooltipViewed } from '@streem/analytics';
import { useRoomSession } from '../../../../context/room_session';
import { useObservable } from '../../../../hooks/rxjs';
export const NetworkSignalLevel = ({ showNetworkQuality }) => {
    const [networkLevelIcon, setNetworkLevelIcon] = useState({
        icon: 'AverageOrAboveSignalIcon',
        iconColor: Theme.colors.grey30,
    });
    const [networkLevelToolTip, setNetworkLevelToolTip] = useState(null);
    const roomSession = useRoomSession();
    const [remoteNetworkQuality] = useObservable(roomSession.media.remoteNetworkQuality);
    useEffect(() => {
        if (!showNetworkQuality)
            return;
        const { headerIcon, tooltip } = mapNetworkLevelIconAttributes(remoteNetworkQuality);
        setNetworkLevelIcon(headerIcon);
        setNetworkLevelToolTip(tooltip);
    }, [remoteNetworkQuality, showNetworkQuality]);
    return (_jsx(NetworkSignalLevelIconContainer, { "data-testid": "network-status-icon", children: _jsx(NetworkQualityTooltip, { networkLevelTooltip: networkLevelToolTip, children: _jsx(AppIcon, { name: networkLevelIcon.icon, color: networkLevelIcon.iconColor }) }) }));
};
const NetworkQualityTooltip = ({ networkLevelTooltip, children, }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipId = 'network-status-icon-tooltip';
    const handleMouseEnter = useCallback(() => {
        if (!networkLevelTooltip)
            return;
        setShowTooltip(true);
        recordTooltipViewed(tooltipId);
    }, [networkLevelTooltip]);
    const handleMouseLeave = useCallback(() => {
        setShowTooltip(false);
    }, []);
    return (_jsx(Tooltip, { message: networkLevelTooltip && (_jsxs(NetworkQualityTooltipContentContainer, { gap: `${networkLevelTooltip.gap}`, children: [_jsx(AppIcon, { name: networkLevelTooltip.tooltipIcon, color: networkLevelTooltip.iconColor, width: networkLevelTooltip.width, height: networkLevelTooltip.height }), _jsx(AppText, { color: Theme.colors.black, size: "small", children: networkLevelTooltip.text })] })), showTooltip: showTooltip, distance: 20, offsetValue: 100, placement: "bottom", fallbackPlacements: ['bottom'], maxWidth: "267px", minWidth: "234px", dataTestId: tooltipId, children: cloneElement(children, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
        }) }));
};
const mapNetworkLevelIconAttributes = (level) => {
    if (level === undefined || level === null) {
        return {
            headerIcon: {
                icon: 'AverageOrAboveSignalIcon',
                iconColor: Theme.colors.grey30,
            },
            tooltip: null,
        };
    }
    if (level >= 3)
        return {
            headerIcon: {
                icon: 'AverageOrAboveSignalIcon',
                iconColor: Theme.colors.blue50,
            },
            tooltip: {
                tooltipIcon: 'CheckmarkIcon',
                iconColor: Theme.colors.green50,
                text: `${level}/5 ${level === 5 ? 'Excellent' : level === 4 ? 'Good' : 'Average'} Network Connection`,
                ...checkmarkIconStyles,
            },
        };
    if (level == 2)
        return {
            headerIcon: {
                icon: 'BelowAverageSignalIcon',
                iconColor: Theme.colors.blue50,
            },
            tooltip: {
                tooltipIcon: 'AlertIcon',
                iconColor: Theme.colors.orange,
                text: '2/5 Below Average Network Connection',
                ...alertIconStyles,
            },
        };
    if (level == 1)
        return {
            headerIcon: {
                icon: 'BadSignalIcon',
                iconColor: Theme.colors.blue50,
            },
            tooltip: {
                tooltipIcon: 'AlertIcon',
                iconColor: Theme.colors.red50,
                text: '1/5 Bad Network Connection',
                ...alertIconStyles,
            },
        };
    else
        return {
            headerIcon: {
                icon: 'LostSignalIcon',
                iconColor: Theme.colors.grey30,
            },
            tooltip: {
                tooltipIcon: 'AlertIcon',
                iconColor: Theme.colors.red50,
                text: '0/5 No Network Connection',
                ...alertIconStyles,
            },
        };
};
const alertIconStyles = {
    width: '23px',
    height: '23px',
    gap: '8px',
};
const checkmarkIconStyles = {
    width: '18px',
    height: '16px',
    gap: '15px',
};
const NetworkSignalLevelIconContainer = styled(Flex)(() => ({
    height: '22px',
    width: '24px',
}));
const NetworkQualityTooltipContentContainer = styled(Flex)(({ gap }) => ({
    gap: `${gap}`,
    alignItems: 'center',
    paddingLeft: '7px',
    height: '36px',
}));
//# sourceMappingURL=network_signal_level.js.map